import _get from 'lodash.get';
import moment from 'moment';
import { DateFormatter } from './date-utils.service';
import { ValueListsService } from './value-lists.service';

export abstract class Marshaller {
  protected constructor(
    private valueListsService: ValueListsService,
    private dateFormatter: DateFormatter
  ) {}

  public marshall(formValue: any): any {
    return {};
  }

  public unmarshall(backendObject: any): any {
    return {};
  }

  public marshallAlphanumericOnly(formValue: any) : {} {
    return {};
  }

  valueListArraySelection(listName: string, ...keys: string[]) {
    const ret: any[] = [];
    if (keys != null) {
      keys.forEach((key: string) => {
        if (key != null && key.trim() !== '') {
          // Schlüssel trimmen, bevor sie weiterverarbeitet werden
          const selection = this.valueListSelection(listName, key.trim());
          if (selection !== undefined) {
            ret.push(selection);
          }
        }
      });
    }
    return ret.length > 0 ? ret : undefined;
  }


  valueListSelection(listName: string, key: string) {
    if (key == null || key.trim() === '') {
      return undefined;
    }
    return {
      valueListID: this.valueListsService.getValueListId(listName),
      valueListItemKey: key
    };
  }

  getFormattedDatePickerValue(datePickerValue: any): string | undefined {
    return this.dateFormatter.getFormattedDatePickerValue(datePickerValue);
  }

  get(rawValue: any) {
    if (rawValue) {
      if (typeof rawValue === 'string') {
        return rawValue.trim();
      }
      return rawValue;
    }
    return undefined;
  }

  stringAsDatepickerModel(value: string): any | undefined {
    let res;
    if (value) {
      const myMoment = moment(value, 'DD.MM.YYYY');
      if (myMoment.isValid()) { // Überprüfung, ob das Datum gültig ist
        res = {
          isRange: false,
          singleDate: {
            date: {
              year: myMoment.year(),
              month: myMoment.month() + 1,
              day: myMoment.date()
            }
          }
        };
      }
    }
    return res;
  }

  getAntragsart(backendObject: any): string {
    let ret : string = '';
    if (backendObject && backendObject.visumAntragsart) {
      ret = backendObject.visumAntragsart;
    }
    return ret;
  }

  marshallReferenz(formValue: any): any {
    let result: undefined;

    const key = +this.getReferenzArt(formValue).valueListItemKey;

    switch (key) {
      case 1:
        result = this.marshallKeineReferenzperson(formValue);
        break;
      case 2:
        result = this.marshallReferenzperson(formValue);
        break;
      case 3:
        result = this.marshallOrganisation(formValue);
        break;
      case 4:
        result = this.marshallHotel(formValue);
        break;
      case 5:
        result = this.marshallAkkreditierterDiplomat(formValue);
        break;
      case 6:
        result = this.marshallEuBuerger(formValue);
        break;
      case 7:
        result = this.marshallWohungsinhaber(formValue);
        break;
      case 8:
        result = this.marshallGewerblicheAnmietung(formValue);
        break;
      case 9:
        result = this.marshallPrivateAnmietung(formValue);
        break;
      default:
        break;
    }
    return result;
  }

  abstract marshallKeineReferenzperson(formValue: any): any;
  abstract marshallReferenzperson(formValue: any): any;
  abstract marshallOrganisation(formValue: any): any;
  abstract marshallHotel(formValue: any): any;
  abstract marshallAkkreditierterDiplomat(formValue: any): any;
  abstract marshallEuBuerger(formValue: any): any;
  abstract marshallWohungsinhaber(formValue: any): any;
  abstract marshallGewerblicheAnmietung(formValue: any): any;
  abstract marshallPrivateAnmietung(formValue: any): any;

  marshallVerwandter(formValue: any): any {
    let result: any;

    if (this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) === true) {
      result = {
        familienname: this.get(formValue.angabenZurPerson.verwandter.familienname),
        vorname: this.get(formValue.angabenZurPerson.verwandter.vorname),
        geburtsdatum: this.get(formValue.angabenZurPerson.verwandter.geburtsdatum),
        staatsangehoerigkeit: this.valueListSelection(
          'land',
          formValue.angabenZurPerson.verwandter.staatsangehoerigkeit
        ),
      };
    } else {
      result = undefined;
    }
    return result;
  }

  protected isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue: any): boolean {
    return (
      this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) !== true &&
      formValue.referenz.art !== '1'
    );
  }

  getReferenzArt(formValue: any): any {
    let result: any;

    if (this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) === true) {
      result = this.valueListSelection('referenz_art', '6');
    } else {
      result = this.valueListSelection('referenz_art', formValue.referenz.art);
    }
    return result;
  }

  // Generic method for constructing 'ansprechpartner'
  protected getAnsprechpartner(formValue: any, basePath: string, additionalFields: any = {}): any {
    return {
      familienname: this.get(_get(formValue, `${basePath}.familienname`)),
      vorname: this.get(_get(formValue, `${basePath}.vorname`)),
      geschlecht: this.valueListSelection('geschlecht', _get(formValue, `${basePath}.geschlecht`)),
      geburtsdatum: this.get(_get(formValue, `${basePath}.geburtsdatum`)),
      geburtsort: this.get(_get(formValue, `${basePath}.geburtsort`)),
      staatsangehoerigkeit: this.valueListSelection(
        'land',
        _get(formValue, `${basePath}.staatsangehoerigkeit`)
      ),
      anschrift: {
        strasse: this.get(_get(formValue, `${basePath}.strasse`)),
        hausnummer: this.get(_get(formValue, `${basePath}.hausnummer`)),
        plz: this.get(_get(formValue, `${basePath}.plz`)),
        ort: this.get(_get(formValue, `${basePath}.ort`)),
        land: this.valueListSelection('land', _get(formValue, `${basePath}.land`))
      },
      kontaktdaten: {
        email: this.get(_get(formValue, `${basePath}.email`)),
        telefon: this.get(_get(formValue, `${basePath}.telefon`))
      },
      ...additionalFields
    };
  }

  protected getAnsprechpartnerFuerReferenzReferenzperson(formValue: any) {
    const basePath = 'referenz.einladendePerson_referenzPerson';
    const additionalFields = {
      abweichendeSchreibweiseNachname: this.get(
        _get(formValue, `${basePath}.abweichendNachname`)
      ),
      abweichendeSchreibweiseVorname: this.get(_get(formValue, `${basePath}.abweichendVorname`)),
      andereNamen: this.get(_get(formValue, `${basePath}.andereNamen`)),
      fruehereNamen: this.get(_get(formValue, `${basePath}.fruehereNamen`))
    };
    return this.getAnsprechpartner(formValue, basePath, additionalFields);
  }

  protected getAnsprechpartnerFuerReferenzOrganisation(formValue: any) {
    const basePath = 'referenz.einladendeOrganisation_Bildungseinrichtung';
    return this.getAnsprechpartner(formValue, basePath);
  }

  protected getAnsprechpartnerFuerReferenzAkkreditierterDiplomat(formValue: any) {
    const basePath = 'referenz.akkreditierterDiplomat';
    return this.getAnsprechpartner(formValue, basePath);
  }

  protected getAnsprechpartnerFuerReferenzGewerblicheAnmietung(formValue: any) {
    const basePath = 'referenz.anmietungGewerblich';
    return this.getAnsprechpartner(formValue, basePath);
  }

  protected getAnsprechpartnerFuerReferenzPrivateAnmietung(formValue: any) {
    const basePath = 'referenz.anmietungPrivat';
    const additionalFields = {
      andereNamen: this.get(_get(formValue, `${basePath}.andereNamen`))
    };
    return this.getAnsprechpartner(formValue, basePath, additionalFields);
  }

  // Generic method for constructing 'organisation'
  protected getOrganisation(formValue: any, basePath: string): any {
    return {
      name: this.get(_get(formValue, `${basePath}.orgaName`)),
      sitz: this.get(_get(formValue, `${basePath}.orgaSitz`)),
      land: this.valueListSelection('land', _get(formValue, `${basePath}.orgaSitzLand`)),
      wirkungsbereich: this.valueListSelection(
        'organisation_wirkungsbereich',
        _get(formValue, `${basePath}.orgaAufgabe`)
      ),
      registerName: this.valueListSelection(
        'organisation_register',
        _get(formValue, `${basePath}.orgaRegisterName`)
      ),
      registerOrt: this.get(_get(formValue, `${basePath}.orgaRegisterOrt`)),
      registerNummer: this.get(_get(formValue, `${basePath}.orgaRegisternummer`)),
      reservierungsnummer: this.get(_get(formValue, `${basePath}.reservierungsnummer`))
    };
  }

  // Mmethods using the generic 'getOrganisation'
  protected getOrganisationFuerReferenzOrganisation(formValue: any) {
    const basePath = 'referenz.einladendeOrganisation_Bildungseinrichtung';
    return this.getOrganisation(formValue, basePath);
  }

  protected getOrganisationFuerReferenzGewerblicheAnmietung(formValue: any) {
    const basePath = 'referenz.anmietungGewerblich';
    return this.getOrganisation(formValue, basePath);
  }

  // 'unmarshall' methods using generic functions
  unmarshallReferenz(backendObject: any): any {
    let result: any;
    const art = _get(backendObject, 'referenz.referenzArt.valueListItemKey', null);

    switch (+art) {
      case 1:
        result = { art };
        break;
      case 2:
        result = this.unmarshallReferenzperson(backendObject);
        break;
      case 3:
        result = this.unmarshallBildungseinrichtung(backendObject);
        break;
      case 4:
        result = this.unmarshallHotel(backendObject);
        break;
      case 5:
        result = this.unmarshallAkkreditierterDiplomat(backendObject);
        break;
      case 6:
        result = this.unmarshallEuBuerger(backendObject);
        break;
      case 7:
        result = this.unmarshallWohnungsinhaber(backendObject);
        break;
      case 8:
        result = this.unmarshallGewerblicheAnmietung(backendObject);
        break;
      case 9:
        result = this.unmarshallPrivateAnmietung(backendObject);
        break;
      default:
        break;
    }
    return result;
  }

  // Generic 'unmarshallAnsprechpartner' method
  protected unmarshallAnsprechpartner(backendObject: any, basePath: string, additionalFields: any = {}): any {
    return {
      familienname: _get(backendObject, `${basePath}.familienname`, null),
      vorname: _get(backendObject, `${basePath}.vorname`, null),
      geschlecht: _get(backendObject, `${basePath}.geschlecht.valueListItemKey`, null),
      geburtsdatum: _get(backendObject, `${basePath}.geburtsdatum`, null),
      geburtsort: _get(backendObject, `${basePath}.geburtsort`, null),
      staatsangehoerigkeit: _get(backendObject, `${basePath}.staatsangehoerigkeit.valueListItemKey`, null),
      strasse: _get(backendObject, `${basePath}.anschrift.strasse`, null),
      hausnummer: _get(backendObject, `${basePath}.anschrift.hausnummer`, null),
      plz: _get(backendObject, `${basePath}.anschrift.plz`, null),
      ort: _get(backendObject, `${basePath}.anschrift.ort`, null),
      land: _get(backendObject, `${basePath}.anschrift.land.valueListItemKey`, null),
      telefon: _get(backendObject, `${basePath}.kontaktdaten.telefon`, null),
      email: _get(backendObject, `${basePath}.kontaktdaten.email`, null),
      ...additionalFields
    };
  }

  // Generic 'unmarshallOrganisation' method
  protected unmarshallOrganisation(backendObject: any, basePath: string): any {
    return {
      orgaName: _get(backendObject, `${basePath}.name`, null),
      reservierungsnummer: _get(backendObject, `${basePath}.reservierungsnummer`, null),
      orgaSitz: _get(backendObject, `${basePath}.sitz`, null),
      orgaSitzLand: _get(backendObject, `${basePath}.land.valueListItemKey`, null),
      orgaAufgabe: _get(backendObject, `${basePath}.wirkungsbereich.valueListItemKey`, null),
      orgaRegisterName: _get(backendObject, `${basePath}.registerName.valueListItemKey`, null),
      orgaRegisterOrt: _get(backendObject, `${basePath}.registerOrt`, null),
      orgaRegisternummer: _get(backendObject, `${basePath}.registerNummer`, null)
    };
  }

  unmarshallReferenzperson(backendObject: any): any {
    const basePath : string = 'referenz.ansprechpartner';
    const additionalFields = {
      abweichendNachname: _get(backendObject, `${basePath}.abweichendeSchreibweiseNachname`, null),
      abweichendVorname: _get(backendObject, `${basePath}.abweichendeSchreibweiseVorname`, null),
      verwandtschaftsverhaeltnis: _get(backendObject, 'referenz.verwandtschaftsbeziehungFreitext', null),
      andereNamen: _get(backendObject, `${basePath}.andereNamen`, null),
      fruehereNamen: _get(backendObject, `${basePath}.fruehereNamen`, null)
    };
    return {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      einladendePerson_referenzPerson: this.unmarshallAnsprechpartner(backendObject, basePath, additionalFields)
    };
  }

  unmarshallBildungseinrichtung(backendObject: any): any {
    const basePathOrg : string = 'referenz.organisation';
    const basePathAnspr : string = 'referenz.ansprechpartner';
    const excludeProps : string[] = ['reservierungsnummer', 'geburtsort'];

    const removeExcludedProps = (obj: any, excludedKeys: string[]) => {
      if (!obj || typeof obj !== 'object') return obj;

      return Object.keys(obj).reduce((acc, key) => {
        if (!excludedKeys.includes(key)) {
          // Rekursiv für verschachtelte Objekte
          acc[key] = typeof obj[key] === 'object' ? removeExcludedProps(obj[key], excludedKeys) : obj[key];
        }
        return acc;
      }, {} as any);
    };

    const ansprechpartner = this.unmarshallAnsprechpartner(backendObject, basePathAnspr);

    const result = {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      einladendeOrganisation_Bildungseinrichtung: {
        ...this.unmarshallOrganisation(backendObject, basePathOrg),
        ...(ansprechpartner ? { ...ansprechpartner } : {})
      }
    };

    return removeExcludedProps(result, excludeProps);
  }

  unmarshallHotel(backendObject: any): any {
    const basePathOrg = 'referenz.organisation';
    const basePathAnspr = 'referenz.ansprechpartner';
    return {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      hotel: {
        ...this.unmarshallOrganisation(backendObject, basePathOrg),
        ...this.unmarshallAnsprechpartner(backendObject, basePathAnspr)
      }
    };
  }

  unmarshallAkkreditierterDiplomat(backendObject: any): any {
    const basePathOrg = 'referenz.organisation';
    const basePathAnspr = 'referenz.ansprechpartner';
    return {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      akkreditierterDiplomat: {
        orgaName: _get(backendObject, `${basePathOrg}.name`, null),
        ...this.unmarshallAnsprechpartner(backendObject, basePathAnspr)
      }
    };
  }

  unmarshallEuBuerger(backendObject: any): any {
    const basePath = 'referenz.ansprechpartner';
    const additionalFields = {
      abweichendNachname: _get(backendObject, `${basePath}.abweichendeSchreibweiseNachname`, null),
      abweichendVorname: _get(backendObject, `${basePath}.abweichendeSchreibweiseVorname`, null),
      andereNamen: _get(backendObject, `${basePath}.andereNamen`, null),
      fruehereNamen: _get(backendObject, `${basePath}.fruehereNamen`, null)
    };
    return {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      euBuerger: this.unmarshallAnsprechpartner(backendObject, basePath, additionalFields)
    };
  }

  unmarshallWohnungsinhaber(backendObject: any): any {
    const basePath = 'referenz.ansprechpartner';
    const additionalFields = {
      abweichendNachname: _get(backendObject, `${basePath}.abweichendeSchreibweiseNachname`, null),
      abweichendVorname: _get(backendObject, `${basePath}.abweichendeSchreibweiseVorname`, null),
      andereNamen: _get(backendObject, `${basePath}.andereNamen`, null),
      fruehereNamen: _get(backendObject, `${basePath}.fruehereNamen`, null)
    };
    return {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      wohnungsInhaber: this.unmarshallAnsprechpartner(backendObject, basePath, additionalFields)
    };
  }

  unmarshallPrivateAnmietung(backendObject: any): any {
    const basePath = 'referenz.ansprechpartner';
    const additionalFields = {
      andereNamen: _get(backendObject, `${basePath}.andereNamen`, null)
    };
    return {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      anmietungPrivat: this.unmarshallAnsprechpartner(backendObject, basePath, additionalFields)
    };
  }

  unmarshallGewerblicheAnmietung(backendObject: any): any {
    const basePathOrg = 'referenz.organisation';
    const basePathAnspr = 'referenz.ansprechpartner';
    return {
      art: _get(backendObject, 'referenz.referenzArt.valueListItemKey', null),
      anmietungGewerblich: {
        ...this.unmarshallOrganisation(backendObject, basePathOrg),
        ...this.unmarshallAnsprechpartner(backendObject, basePathAnspr)
      }
    };
  }
}
